/**
 * API client for the member portal.
 *
 * Same-origin by LOCKED architectural decision (blueprint §4): the SPA and its
 * API are served from one hostname, with the API under `/api/*`. That removes
 * CORS preflight latency and an entire class of CSRF complexity.
 *
 * Consequences that are easy to undo by accident, so they are stated here:
 *
 *  - **The base URL is a relative path.** Never an absolute origin. If you find
 *    yourself needing one, the proxy is misconfigured and pointing the client
 *    elsewhere papers over it.
 *  - **`credentials: 'same-origin'`.** The session is an HttpOnly cookie; no
 *    token is ever exposed to JavaScript, so there is nothing for an XSS to
 *    read.
 *  - **No `mode: 'cors'`, no custom preflight-triggering headers** beyond
 *    `Content-Type`, which is on the safelist for JSON.
 */

import type { components } from './schema.gen';
import { normalizePasswordInput } from '../lib/password';

export type Problem = components['schemas']['Problem'];

const BASE = '/api';

/** An RFC 9457 error from the server, carried as a typed exception. */
export class ApiError extends Error {
  constructor(
    readonly problem: Problem,
    readonly status: number,
  ) {
    super(problem.title);
    this.name = 'ApiError';
  }

  /**
   * Branch on this, never on `detail`.
   *
   * `type` is part of the contract and stable. `detail` is prose for humans and
   * may be reworded at any time.
   */
  is(type: string): boolean {
    return this.problem.type === type;
  }
}

/** Thrown when the network failed — distinct from the server refusing. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('could not reach the server');
    this.name = 'NetworkError';
    this.cause = cause;
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /**
   * Required by the contract on anything that moves money. Generating it in the
   * client rather than the server is the point: a retry after a timeout must
   * reuse the *same* key, or the customer is charged twice.
   */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, idempotencyKey, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  if (response.status === 204) return undefined as T;

  const isProblem = response.headers.get('content-type')?.includes('application/problem+json');

  if (!response.ok) {
    if (isProblem) {
      throw new ApiError((await response.json()) as Problem, response.status);
    }
    // A non-problem error body means something upstream of the application
    // answered — a proxy 502, an HTML error page. Do not pretend it is ours.
    throw new ApiError(
      {
        type: '/problems/unexpected',
        title: 'Unexpected response',
        status: response.status,
        detail: `the server returned ${response.status} without a problem document`,
      },
      response.status,
    );
  }

  return (await response.json()) as T;
}

/**
 * An idempotency key for a money-moving request.
 *
 * Generate it once, when the user commits to the action, and reuse it across
 * retries. Generating a fresh one per attempt defeats the entire mechanism.
 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID().replace(/-/g, '') + crypto.randomUUID().replace(/-/g, '').slice(0, 8);
}

// -- typed endpoints ------------------------------------------------------
// Response types come from the generated schema, so a contract change that
// breaks the portal fails `tsc` rather than surfacing at runtime.

type Paths = components['schemas'];

/**
 * The two page shapes, straight from the contract.
 *
 * `PageInfo` is keyset — a cursor and `has_more`, no total, for the lists that
 * gain rows while you read them. `OffsetPageInfo` is page numbers with a
 * count, for the bounded catalogues. Which endpoint uses which is a decision
 * recorded in ADR-0009, not a choice a caller gets to make here.
 */
export type PageInfo = Paths['PageInfo'];
export type OffsetPageInfo = Paths['OffsetPageInfo'];

/** The five notification types the history page's filter and the bell's items switch on. */
export type NotificationType = Paths['Notification']['type'];

/** Server-side maximum. Asking for more is a 400, not a clamp. */
export const MAX_PAGE_SIZE = 200;

function qs(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export const api = {
  me: () => request<Paths['Me']>('/me'),

  /**
   * Switch which organization this session scopes to (LLMR-54). Membership
   * is checked server-side; a foreign `org_id` is 403. Takes effect for this
   * session only.
   */
  setActiveOrg: (orgId: string) =>
    request<Paths['Me']>('/me/active-org', {
      method: 'PUT',
      body: { org_id: orgId },
    }),

  /**
   * Partial profile update. Returns the refreshed Me. Avatar accepts a data
   * URL or null to clear; username is set-once on the server.
   */
  updateProfile: (body: {
    display_name?: string | null;
    username?: string;
    date_of_birth?: string | null;
    phone_e164?: string | null;
    avatar_data_url?: string | null;
  }) =>
    request<Paths['Me']>('/me', {
      method: 'PATCH',
      body,
    }),

  /**
   * Change the signed-in user's password. Requires the current password.
   * Answers 204 on success; wrong current password is `/problems/wrong-password`.
   */
  changePassword: (currentPassword: string, newPassword: string) =>
    request<void>('/me/password', {
      method: 'POST',
      body: {
        current_password: normalizePasswordInput(currentPassword),
        new_password: normalizePasswordInput(newPassword),
      },
    }),

  /** Which second factors this account may use, has enrolled, and whether one is mandatory. */
  twoFactorMethods: () =>
    request<{
      available: components['schemas']['TwoFactorMethod'][];
      enrolled: components['schemas']['TwoFactorMethod'][];
      enforced: boolean;
    }>('/me/2fa/methods'),

  /**
   * Begins enrolling a second factor. For `totp`, the response carries the
   * secret and otpauth:// URI exactly once — nothing else re-fetches it. For
   * `sms`/`email_otp` (LLMR-66) it ALSO sends a code immediately, to the
   * destination LLMR-25 or signup already verified — `destination_masked`/
   * `expires_in_seconds` are present instead. 403 means this account type
   * isn't eligible for the requested method; 409 means it's already
   * enrolled (see ApiError.is in the caller).
   */
  enrollTwoFactor: (method: components['schemas']['TwoFactorMethod']) =>
    request<{
      enrollment_id: string;
      totp_secret?: string;
      totp_uri?: string;
      destination_masked?: string;
      expires_in_seconds?: number;
    }>('/me/2fa/enroll', {
      method: 'POST',
      body: { method },
    }),

  /**
   * Proves the caller can generate real codes from the secret /enroll just
   * issued. Recovery codes are returned once, here, and never again —
   * the caller must force an explicit save/acknowledge step before letting
   * this response go.
   */
  confirmTwoFactorEnrollment: (enrollmentId: string, code: string) =>
    request<{ recovery_codes: string[] }>('/me/2fa/confirm', {
      method: 'POST',
      body: { enrollment_id: enrollmentId, code },
    }),

  /**
   * LLMR-76. Queues a fresh re-verification code for a CONFIRMED
   * sms/email_otp method ahead of disabling it. Not called for `totp` — its
   * disable flow asks for the current authenticator code directly.
   */
  sendTwoFactorDisableCode: (method: components['schemas']['TwoFactorMethod']) =>
    request<{ destination_masked: string; expires_in_seconds: number }>(
      `/me/2fa/${encodeURIComponent(method)}/disable/send`,
      { method: 'POST', body: {} },
    ),

  /**
   * LLMR-76. Removes a confirmed second factor. `code` must prove control of
   * the SAME method being removed. Returns the account's remaining
   * confirmed factors so the caller can refresh in one round trip.
   */
  disableTwoFactor: (method: components['schemas']['TwoFactorMethod'], code: string) =>
    request<{ enrolled: components['schemas']['TwoFactorMethod'][] }>(
      `/me/2fa/${encodeURIComponent(method)}`,
      { method: 'DELETE', body: { code } },
    ),

  /**
   * LLMR-76. One page of the caller's own recent security events. Omitting
   * `category` mixes every category together — the profile page's filter
   * dropdown defaults to that and narrows to one category on selection.
   * Offset-paged like team()/models(), not keyset: a member's own event
   * count here is always the kind of small, bounded catalogue OffsetPageInfo
   * is for.
   */
  securityHistory: (params: {
    category?: 'two_factor' | 'password' | 'recovery_code' | 'session';
    page?: number;
    page_size?: number;
  }) =>
    request<{ events: { action: string; occurred_at: string }[]; page: OffsetPageInfo }>(
      `/me/security-history${qs(params)}`,
    ),

  /**
   * The bell dropdown's poll (v1) and the full history page's list (v2)
   * share this one call. `unread_count` is never capped by `page_size` and
   * never narrowed by `type` — it is always every unread notification,
   * regardless of what this particular call filtered to or how many rows
   * it asked for.
   */
  notifications: (
    params: {
      type?: NotificationType;
      page?: number;
      page_size?: number;
    } = {},
  ) =>
    request<{ data: Paths['Notification'][]; page: OffsetPageInfo; unread_count: number }>(
      `/me/notifications${qs(params)}`,
    ),

  /** "Open the bell" — marks every currently-unread notification as read. */
  markNotificationsRead: () => request<void>('/me/notifications/read', { method: 'POST' }),

  /**
   * Sends a verification code to the phone already on the profile.
   *
   * It does NOT take a number: PATCH /me is the only writer of `phone_e164`,
   * and changing it there clears any verification. So the flow for a new
   * number is always save, then verify.
   *
   * Every call costs a real SMS, which is why the server limits it on three
   * axes and over two windows. 409 means there is no phone to verify or it is
   * already verified, 422 that the number cannot receive SMS, 429 that the
   * caller is throttled, 503 that SMS is unconfigured or the gateway is down.
   */
  startPhoneVerification: () =>
    request<{ code_id: string; phone_masked: string; expires_in_seconds: number }>(
      '/me/phone/verify/start',
      { method: 'POST', body: {} },
    ),

  /**
   * Confirms the code. `codeId` is threaded through from the start response
   * rather than resolved server-side as "the most recent" — two starts racing
   * would otherwise check the wrong one's code.
   *
   * On success `phone_country` is set, which is what makes `sms` appear in
   * twoFactorMethods().available.
   */
  confirmPhoneVerification: (codeId: string, code: string) =>
    request<{ phone_masked: string; phone_country: string }>('/me/phone/verify/confirm', {
      method: 'POST',
      body: { code_id: codeId, code },
    }),

  balance: () =>
    request<{ balance: Paths['Money']; updated_at: string }>('/balance'),

  // Open to every role (LLMR-55) — "is there money left", not "how much".
  // See handleBalanceStatus in member-api for why this is a separate
  // endpoint from GET /balance rather than a role-conditional shape on it.
  balanceStatus: () => request<{ depleted: boolean; ever_funded: boolean }>('/balance/status'),

  /**
   * The org's most recently submitted full-invoice profile, if any —
   * backs "use latest tax invoice info" on the top-up form. `exists: false`
   * means this org has never submitted a full invoice.
   */
  taxInvoiceProfile: () => request<Paths['TaxInvoiceProfile']>('/tax-invoice-profile'),

  // LLMR-55 follow-up: a plain member cannot top up directly, so this is
  // how they ask an admin/owner to. Shares one cooldown per org — a second
  // call inside the window 429s, regardless of reason. Omit recipientUserID
  // to notify every admin/owner; pass one to ask a specific person instead
  // (must be an admin/owner of the caller's org, or the server 400s).
  //
  // `reason` (LLMR-70) only changes the email copy server-side — 'balance'
  // (default) is the original "I'm out of credit" case; 'quota' is "this
  // week's free-model quota is exhausted and auto-fallback is off, so I'm
  // blocked."
  requestTopUp: (recipientUserID?: string, reason?: 'balance' | 'quota') => {
    const body: { recipient_user_id?: string; reason?: string } = {};
    if (recipientUserID) body.recipient_user_id = recipientUserID;
    if (reason && reason !== 'balance') body.reason = reason;
    return request<{ status: string }>('/balance/request-topup', {
      method: 'POST',
      body: Object.keys(body).length ? body : undefined,
    });
  },

  // giveaway: redeem a printed card's code for credit.
  // The amount is a surprise by design (llmr-control-api's own comment) — the
  // server never reveals it until this call succeeds.
  redeemPromoCode: (code: string) =>
    request<{ amount: { amount: string; currency: string } }>('/promo/redeem', {
      method: 'POST',
      body: { code },
    }),

  // LLMR-61: whether the org falls back to paid billing (vs. being blocked)
  // once its weekly free-model quota is exhausted. GET is open to every
  // role, same as balanceStatus; PATCH 403s for a plain member.
  quotaStatus: () =>
    request<{
      tokens_used: number;
      tokens_cap: number;
      requests_used: number;
      requests_cap: number;
      resets_at: string;
    }>('/quota/status'),

  autoFallback: () => request<{ enabled: boolean }>('/quota/auto-fallback'),

  setAutoFallback: (enabled: boolean) =>
    request<{ enabled: boolean }>('/quota/auto-fallback', { method: 'PATCH', body: { enabled } }),

  keys: (params: { cursor?: string; limit?: number } = {}) =>
    request<{ data: Paths['ApiKey'][]; page: PageInfo }>(`/keys${qs(params)}`),

  createKey: (name: string, idempotencyKey: string, expiresAt?: string, note?: string) =>
    request<Paths['ApiKey'] & { secret: string }>('/keys', {
      method: 'POST',
      // Each optional field is OMITTED from the body rather than sent as
      // `undefined` when not chosen — the server already defaults an absent
      // `expires_at` to "never expires" (contract), so there is no
      // client-side default worth duplicating. routing_policy is not a
      // request field at all any more: every key inherits its org's
      // staff-set default. `JSON.stringify` drops `undefined`-valued keys on
      // its own; this object literal relies on exactly that. An empty/
      // whitespace `note` is likewise omitted so the server stores null
      // rather than "".
      body: {
        name,
        expires_at: expiresAt,
        note: note?.trim() || undefined,
      },
      idempotencyKey,
    }),

  revokeKey: (keyId: string) =>
    request<void>(`/keys/${encodeURIComponent(keyId)}`, { method: 'DELETE' }),

  /**
   * A partial update: a field left OUT of `fields` (i.e. `undefined`) is
   * left unchanged; a field present and `null` is cleared back to the
   * gateway's own default (no rate limit / never expires). This mirrors the
   * server's own JSON-merge-patch semantics exactly — see
   * store.UpdateAPIKeyFields on the Go side for why "absent" and "null" have
   * to mean different things once there are two independently-clearable
   * fields on one body. `JSON.stringify` already drops `undefined` keys, so
   * passing this object straight through as the body is what makes that
   * distinction survive onto the wire.
   */
  updateKey: (keyId: string, fields: { rateLimitRPM?: number | null; expiresAt?: string | null }) =>
    request<Paths['ApiKey']>(`/keys/${encodeURIComponent(keyId)}`, {
      method: 'PATCH',
      body: { rate_limit_rpm: fields.rateLimitRPM, expires_at: fields.expiresAt },
    }),

  /**
   * Usage events, keyset-paged.
   *
   * `from`/`to` are now applied in SQL — they were declared by the contract and
   * ignored by the server until ADR-0009, which is why this screen used to show
   * events outside the range its own date pickers were showing.
   */
  usage: (params: {
    from?: string;
    to?: string;
    limit?: number;
    cursor?: string;
    key_id?: string;
    status?: 'success' | 'failed';
    /** LLMR-141. Owner/admin only — ignored (forced to the caller) otherwise. */
    user_id?: string;
  } = {}) => request<{ data: Paths['UsageEvent'][]; page: PageInfo }>(`/usage${qs(params)}`),

  /**
   * Aggregated spend for the summary view. `from`/`to` are required by the
   * contract — the caller picks a range, there is no "everything" default.
   */
  usageSummary: (params: {
    from: string;
    to: string;
    group_by?: 'model' | 'key' | 'day';
    /** LLMR-141. Owner/admin only — ignored (forced to the caller) otherwise. */
    user_id?: string;
  }) =>
    request<{
      total: Paths['Money'];
      buckets: {
        label: string;
        /** Only present when `group_by=key`; may still be null there. */
        name?: string | null;
        cost: Paths['Money'];
        requests: number;
        /** completed + interrupted — both billable. */
        successful_requests: number;
        failed_requests: number;
        input_tokens?: number;
        output_tokens?: number;
      }[];
    }>(`/usage/summary${qs(params)}`),

  /**
   * The model catalogue, offset-paged with a total.
   *
   * Search, filters and sort are SERVER parameters. They used to be applied in
   * the page component to whatever `/models` had returned, which meant a
   * customer searching a 300-model catalogue was searching the subset the
   * component happened to hold.
   */
  models: (
    params: {
      page?: number;
      page_size?: number;
      sort?: 'model_id' | 'display_name' | 'price' | 'price_output';
      order?: 'asc' | 'desc';
      q?: string;
      provider?: Paths['Provider'];
      region?: Paths['Region'];
      // LLMR-136: what call shape a model accepts. Lets a caller ask for one
      // — the Models page filter and the Playground's chat picker both use
      // this instead of filtering the unfiltered list themselves. 'image'
      // and 'video' are reserved for the not-yet-built generation epic; no
      // rows carry them today.
      mode?: 'chat' | 'embedding' | 'image' | 'video';
    } = {},
  ) => request<{ data: Paths['Model'][]; page: OffsetPageInfo }>(`/models${qs(params)}`),

  /** Public, static reference data for the cascading address picker (e-Tax invoice). */
  geo: {
    provinces: () => request<{ data: Paths['Province'][] }>('/geo/provinces'),
    districts: (provinceCode: number) => request<{ data: Paths['District'][] }>(`/geo/districts${qs({ province_code: provinceCode })}`),
    subdistricts: (districtCode: number) =>
      request<{ data: Paths['Subdistrict'][] }>(`/geo/subdistricts${qs({ district_code: districtCode })}`),
  },

  /**
   * The caller's organization roster, offset-paged. A team is built by hand,
   * one invite at a time, so a generous default limit means the caller's own
   * row is virtually always on page 1 — which the Team screen relies on to
   * work out its own role rather than duplicating the server's
   * primary-organization resolution.
   */
  team: (params: { page?: number; page_size?: number; sort?: 'role' | 'created_at' | 'email'; order?: 'asc' | 'desc' } = {}) =>
    request<{
      data: Paths['Member'][];
      page: OffsetPageInfo;
      limits: { owner_admin: Paths['TeamLimitGroup']; member: Paths['TeamLimitGroup'] };
    }>(`/team${qs(params)}`),

  /**
   * Always 202 `{status: 'invitation_sent'}` — membership only lands after
   * the invitee accepts the emailed link (including re-inviting a previously
   * removed member). Team.tsx refreshes the pending-invites list on success.
   *
   * `resend` must be true for a deliberate resend of an already-pending
   * invite (see `resendInvite` below) — omitted or false, a pending invite
   * for this email answers 409 `/problems/invite-already-pending` instead of
   * silently refreshing it, since that would invalidate a link that may
   * already be in the recipient's inbox.
   */
  addMember: (email: string, role?: 'member' | 'admin', expiresInDays?: number, resend?: boolean) =>
    request<{ status: 'invitation_sent'; email: string }>('/team', {
      method: 'POST',
      body: {
        email,
        ...(role ? { role } : {}),
        ...(expiresInDays ? { expires_in_days: expiresInDays } : {}),
        ...(resend ? { resend: true } : {}),
      },
    }),

  /** Resend is just addMember with `resend: true` — see its doc comment. */
  resendInvite: (email: string, role: 'member' | 'admin') => api.addMember(email, role, undefined, true),

  updateMemberRole: (userId: string, role: 'member' | 'admin') =>
    request<Paths['Member']>(`/team/${encodeURIComponent(userId)}`, { method: 'PATCH', body: { role } }),

  removeMember: (userId: string) =>
    request<void>(`/team/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  /** Owner/admin only — a plain member cannot see who has been invited but hasn't joined yet. */
  pendingInvites: (params: { page?: number; page_size?: number; sort?: 'created_at' | 'expires_at' | 'email'; order?: 'asc' | 'desc' } = {}) =>
    request<{ data: Paths['PendingInvite'][]; page: OffsetPageInfo }>(`/team/invites${qs(params)}`),

  /**
   * LLMR-139. Owner/admin only, same gate as pendingInvites — every invite
   * that has already concluded (accepted / revoked / expired) for this
   * organization, since pendingInvites simply stops listing a row the
   * moment it isn't outstanding anymore.
   */
  inviteHistory: (params: { page?: number; page_size?: number } = {}) =>
    request<{ data: Paths['InviteHistoryEntry'][]; page: OffsetPageInfo }>(`/team/invites/history${qs(params)}`),

  /**
   * Cancels a pending invite. Admin-cannot-touch-admin asymmetry — a 403
   * here means the target invite is `admin`-role and only the owner can
   * cancel it (unlike creating or resending one via `addMember`).
   */
  revokeInvite: (inviteId: string) =>
    request<void>(`/team/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' }),

  /**
   * Changes a pending invite's role and/or expiry. Same admin-cannot-touch-
   * or-grant-admin asymmetry as `revokeInvite` — a 403 means either side of
   * the edit (current or requested role) is `admin` and the caller isn't
   * the owner. At least one of the two fields must be given.
   */
  updateInvite: (inviteId: string, changes: { role?: 'member' | 'admin'; expiresInDays?: number }) =>
    request<Paths['PendingInvite']>(`/team/invites/${encodeURIComponent(inviteId)}`, {
      method: 'PATCH',
      body: {
        ...(changes.role ? { role: changes.role } : {}),
        ...(changes.expiresInDays ? { expires_in_days: changes.expiresInDays } : {}),
      },
    }),

  /**
   * LLMR-per-member-spend-limit Phase 4 (write path — nothing yet enforces
   * these limits; Phase 2/3 are unbuilt). Owner/admin only, and only for a
   * b2b organization — 403 for either. Returns both the org's own default
   * row (no `user_id`) and every member's own override.
   */
  memberBudgets: () => request<{ data: Paths['MemberBudget'][] }>('/team/budgets'),

  /** Upsert: setting the same `periodType` again replaces it, not a second row. */
  setOrgDefaultBudget: (periodType: 'daily' | 'monthly', amount: string) =>
    request<Paths['MemberBudget']>('/team/budgets/default', {
      method: 'PUT',
      body: { period_type: periodType, limit: { amount, currency: 'THB' } },
    }),

  /** Members with their own override are unaffected; members with neither fall back to unlimited. */
  deleteOrgDefaultBudget: (periodType: 'daily' | 'monthly') =>
    request<void>(`/team/budgets/default${qs({ period_type: periodType })}`, { method: 'DELETE' }),

  setMemberBudget: (userId: string, periodType: 'daily' | 'monthly', amount: string) =>
    request<Paths['MemberBudget']>(`/team/${encodeURIComponent(userId)}/budget`, {
      method: 'PUT',
      body: { period_type: periodType, limit: { amount, currency: 'THB' } },
    }),

  /** The member then falls back to the org default, or unlimited if there is none. */
  deleteMemberBudget: (userId: string, periodType: 'daily' | 'monthly') =>
    request<void>(`/team/${encodeURIComponent(userId)}/budget${qs({ period_type: periodType })}`, {
      method: 'DELETE',
    }),

  /**
   * Top-up history for this organization, keyset-paged.
   *
   * Read-only, and it adds no credit — that is still the sole business of the
   * signature-verified webhook. It exists so the Top-up screen survives a
   * refresh with a payment still pending, which it previously did not: the
   * history was React state and vanished with the tab.
   */
  topUps: (params: { cursor?: string; limit?: number; from?: string; to?: string } = {}) =>
    request<{ data: Paths['TopUp'][]; page: PageInfo }>(`/topups${qs(params)}`),

  // `method` defaults to promptpay — materially cheaper than card, so the UI
  // leads with it.
  createTopUp: (
    amount: string,
    idempotencyKey: string,
    taxInvoice: components['schemas']['TaxInvoiceInput'],
    method: 'promptpay' | 'card' = 'promptpay',
  ) =>
    request<Paths['TopUpCreated']>('/topups', {
      method: 'POST',
      body: { amount: { amount, currency: 'THB' }, method, tax_invoice: taxInvoice },
      idempotencyKey,
    }),

  /**
   * Charge the payment source/token the browser tokenized with Omise.
   *
   * The second half of a top-up: exactly one of `source` (the `src_…` id
   * Omise.js returned for PromptPay) or `token` (the `tokn_…` id for a card)
   * is sent. The server holds the single-use credential that authorises the
   * charge, so this call is what turns a created payment into a QR to scan
   * (PromptPay) or a 3-D Secure redirect / silent capture (card).
   *
   * Still adds no credit — that remains the sole business of the
   * signature-verified gateway callback.
   */
  chargeTopUp: (id: string, payload: { source: string } | { token: string }) =>
    request<Paths['TopUpCharged']>(`/topups/${encodeURIComponent(id)}/source`, {
      method: 'POST',
      body: payload,
    }),

  topUp: (id: string) => request<Paths['TopUp']>(`/topups/${encodeURIComponent(id)}`),

  /**
   * Same-origin relay of a top-up's payment_qr — <img src> can already
   * show payment_qr directly, this is only for canvas export (saving a
   * composed PNG with an amount/expiry caption), which needs a
   * same-origin response to avoid a tainted canvas. See saveQrCode
   * (pages/TopUp.tsx) and handleGetTopUpQRImage (member-api).
   */
  topUpQrImageUrl: (id: string) => `${BASE}/topups/${encodeURIComponent(id)}/qr-image`,

  /**
   * LLMR-121: recover an expired/failed top-up the customer says they
   * actually paid, by uploading the bank slip. `qrPayload` is read
   * client-side (lib/slipUpload.ts) and may be empty when the browser
   * could not decode it — the image still uploads either way.
   *
   * Still adds no credit — only a signature-verified webhook, or staff
   * settling a review in llmr-admin-web, ever does that.
   */
  uploadTopUpSlip: (id: string, imageDataUrl: string, qrPayload: string) =>
    request<Paths['TopUpSlip']>(`/topups/${encodeURIComponent(id)}/slip`, {
      method: 'POST',
      body: { image_data_url: imageDataUrl, qr_payload: qrPayload || undefined },
    }),

  topUpSlip: (id: string) => request<Paths['TopUpSlip']>(`/topups/${encodeURIComponent(id)}/slip`),

  /**
   * LLMR-235: the checkbox picker's data source — every paid top-up this
   * org can still claim withholding-tax credit on (>=1,000 THB ex-VAT, no
   * active claim already against it). b2b orgs only.
   */
  whtEligibleTopUps: () => request<{ data: Paths['WHTEligibleTopUp'][] }>('/topups/wht-eligible'),

  /**
   * Submits one 50-Tawi certificate against one or more paid top-ups.
   * `topUpIds` takes one id (a single-transaction claim) or several (a
   * certificate consolidating a whole month) under the same shape — see
   * migration 000084's comment for why this is one code path, not two.
   * Adds no credit itself — only an approved claim (staff review,
   * llmr-admin-web) does that.
   */
  createWHTClaim: (input: {
    docNumber: string;
    docDate: string;
    declaredAmount: string;
    declaredTaxId: string;
    docFileDataUrl: string;
    topUpIds: string[];
  }) =>
    request<{
      claim_id: string;
      status: 'pending_review';
      items: Paths['WHTClaimSubmittedItem'][];
      total_expected_wht: components['schemas']['Money'];
      validation_warnings: string[];
    }>('/topups/wht-claims', {
      method: 'POST',
      body: {
        doc_number: input.docNumber,
        doc_date: input.docDate,
        declared_amount: input.declaredAmount,
        declared_tax_id: input.declaredTaxId,
        doc_file_data_url: input.docFileDataUrl,
        topup_ids: input.topUpIds,
      },
    }),

  /** This org's own withholding-tax claim history, every status, newest first. */
  whtClaims: () => request<{ data: Paths['WHTClaimSummary'][] }>('/topups/wht-claims'),

  login: (email: string, password: string, rememberMe = false) =>
    request<
      | components['schemas']['SessionEstablished']
      | components['schemas']['TwoFactorChallenge']
    >('/auth/login', {
      method: 'POST',
      body: {
        email: email.trim(),
        password: normalizePasswordInput(password),
        remember_me: rememberMe,
      },
    }),

  verifyTwoFactor: (
    challengeId: string,
    code: string,
    isRecoveryCode = false,
    method?: components['schemas']['TwoFactorMethod'],
    rememberMe = false,
  ) =>
    request<components['schemas']['SessionEstablished']>('/auth/2fa/verify', {
      method: 'POST',
      body: {
        challenge_id: challengeId,
        code,
        is_recovery_code: isRecoveryCode,
        method,
        remember_me: rememberMe,
      },
    }),

  /**
   * Queues a login code for an sms/email_otp second factor (LLMR-66). totp
   * needs no send step — the code already sits in the authenticator app —
   * so this is only ever called for the other two methods. Calling it again
   * for the same challenge supersedes whatever code was queued before,
   * which is also how "resend" and "switch method" both work.
   */
  sendTwoFactorOTP: (challengeId: string, method: 'sms' | 'email_otp') =>
    request<{ destination_masked: string; expires_in_seconds: number }>('/auth/2fa/send', {
      method: 'POST',
      body: { challenge_id: challengeId, method },
    }),

  logout: () => request<void>('/auth/logout', { method: 'POST' }),

  /**
   * accountType 'b2b' also requires orgName + taxId (400
   * /problems/org-name-required / /problems/invalid-tax-id otherwise); both
   * are ignored for 'individual' (the default when omitted). Never returns a
   * session either way — the account is unverified until the emailed link is
   * followed, and /auth/login refuses an unverified account (see SignIn's
   * `email-not-verified` branch). The organisation itself (individual or
   * b2b) isn't created by this call — only once that link is followed.
   *
   * A duplicate email is refused with 409 (/problems/email-already-registered).
   * acceptedTerms must be true — the server re-checks this independently
   * (400 /problems/terms-not-accepted) rather than trusting that the caller
   * only reaches this with the checkbox actually checked.
   */
  register: (
    email: string,
    password: string,
    phone: string,
    acceptedTerms: boolean,
    displayName?: string,
    accountType?: 'individual' | 'b2b',
    orgName?: string,
    taxId?: string,
    promoCode?: string,
    referralSource?: string,
  ) =>
    request<{ status: 'verification_sent' }>('/auth/register', {
      method: 'POST',
      body: {
        email: email.trim(),
        password: normalizePasswordInput(password),
        phone,
        accepted_terms: acceptedTerms,
        display_name: displayName || undefined,
        account_type: accountType || undefined,
        org_name: accountType === 'b2b' ? orgName : undefined,
        tax_id: accountType === 'b2b' ? taxId : undefined,
        promo_code: promoCode || undefined,
        referral_source: referralSource || undefined,
      },
    }),

  /** 200 when free, 409 when the address already has an account. */
  checkRegisterEmail: (email: string, signal?: AbortSignal) =>
    request<{ available: true }>('/auth/register/check-email', {
      method: 'POST',
      body: { email },
      signal,
    }),

  /**
   * giveaway: pre-submit validation for the signup
   * form's optional promo code field. Always 200 — `status` is the answer,
   * never an error — and never includes the code's amount; see
   * llmr-control-api's CheckPromoCode for why.
   */
  checkRegisterPromoCode: (code: string, signal?: AbortSignal) =>
    request<{ status: 'valid' | 'not_found' | 'used' | 'not_yet_valid' | 'expired' }>(
      '/auth/register/check-promo-code',
      { method: 'POST', body: { code }, signal },
    ),

  verifyEmail: (token: string) =>
    request<{
      status: 'verified';
      requires_two_factor_setup?: boolean;
      /** Only present when the signup form's promo code redeemed successfully. */
      promo_credit?: { amount: string; currency: string };
    }>('/auth/verify-email', {
      method: 'POST',
      body: { token },
    }),

  /** Always resolves — 202 whether or not the address exists or can reset. */
  resendVerification: (email: string) =>
    request<{ status: 'verification_sent' }>('/auth/verify-email/resend', {
      method: 'POST',
      body: { email },
    }),

  /** Always resolves — 202 whether or not the address exists or can reset. */
  requestPasswordReset: (email: string) =>
    request<{ status: 'reset_sent' }>('/auth/password-reset/request', {
      method: 'POST',
      body: { email },
    }),

  /**
   * Read-only pre-check for the reset-password page to run on load — never
   * consumes the token, unlike confirmPasswordReset. Throws ApiError with
   * TypeInvalidVerificationToken when the link is dead (expired, already
   * used, or superseded by a later request — the server deliberately does
   * not say which, see llmr-member-api's handlePasswordResetValidate).
   */
  validatePasswordReset: (token: string) =>
    request<{ valid: true }>(`/auth/password-reset/validate?token=${encodeURIComponent(token)}`),

  /** Consumes the emailed token and sets a new password. No session is issued. */
  confirmPasswordReset: (token: string, newPassword: string) =>
    request<{ status: 'password_reset' }>('/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, new_password: normalizePasswordInput(newPassword) },
    }),

  /**
   * Read-only pre-check for the accept-invite page to run on load — never
   * consumes the token, unlike acceptInvite. Throws ApiError with
   * TypeInvalidVerificationToken when the link is dead (revoked, expired, or
   * already accepted — the server deliberately does not say which, see
   * llmr-member-api's handleAcceptInviteValidate).
   */
  validateInvite: (token: string) =>
    request<{ valid: true; account_exists: boolean }>(
      `/team/invite/validate?token=${encodeURIComponent(token)}`,
    ),

  /**
   * The invite token is the credential (see llmr-member-api's
   * handleAcceptInvite) — unlike register, this DOES establish a session.
   * Clicking a link only the invited address could have received is already
   * proof of ownership, so there is no separate verify-then-login step; the
   * response shape is byte-identical to a completed login.
   */
  acceptInvite: (token: string, password: string, phone: string, displayName?: string) =>
    request<components['schemas']['SessionEstablished']>('/team/invite/accept', {
      method: 'POST',
      body: {
        token,
        password: normalizePasswordInput(password),
        phone,
        display_name: displayName || undefined,
      },
    }),

  /** Existing-account / re-invite path — token alone; no password or phone. */
  acceptInviteExisting: (token: string) =>
    request<components['schemas']['SessionEstablished']>('/team/invite/accept', {
      method: 'POST',
      body: { token },
    }),
};
